import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useContext, useEffect, useState } from "react";
import { useMutation } from "react-relay";
import { InternScheduledBuildCardCancelMutation } from "./__generated__/InternScheduledBuildCardCancelMutation.graphql";
import { InternScheduledBuildCardTriggerMutation } from "./__generated__/InternScheduledBuildCardTriggerMutation.graphql";
import SnackbarContext from "../../../contexts/SnackbarContext";
import InternCard from "../components/InternCard";
import InternStatusBadge from "../components/InternStatusBadge";

const triggerBuildMutation = graphql`
  mutation InternScheduledBuildCardTriggerMutation(
    $brandId: ID!
    $platform: String!
  ) {
    triggerScheduledBuildNow(brandId: $brandId, platform: $platform) {
      success
      message
      pipelineId
    }
  }
`;

const cancelBuildMutation = graphql`
  mutation InternScheduledBuildCardCancelMutation(
    $brandId: ID!
    $platform: String!
  ) {
    cancelScheduledBuild(brandId: $brandId, platform: $platform) {
      success
      message
    }
  }
`;

interface PendingChange {
  field: string;
  new_value?: string;
  changed_at: string;
}

interface ScheduledBuildData {
  id: string;
  platform: string;
  changesCount: number;
  scheduledFor: string;
  lastChangeAt: string;
  status: string;
  pendingChanges?: PendingChange[];
}

interface Props {
  brandId: string;
  scheduledBuild: ScheduledBuildData | null;
  onRefresh?: () => void;
  /** Render content only — parent provides the card chrome */
  embedded?: boolean;
}

const formatTimeRemaining = (scheduledFor: string): string => {
  const now = new Date();
  const target = new Date(scheduledFor);
  const diff = target.getTime() - now.getTime();

  if (diff <= 0) return "Building soon...";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

const getProgressPercent = (
  lastChangeAt: string,
  scheduledFor: string
): number => {
  const lastChange = new Date(lastChangeAt).getTime();
  const scheduled = new Date(scheduledFor).getTime();
  const now = Date.now();

  const total = scheduled - lastChange;
  const elapsed = now - lastChange;

  return Math.min(100, Math.max(0, (elapsed / total) * 100));
};

const formatFieldName = (field: string): string =>
  field
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/** Unique field names in order of first appearance. */
const uniqueChangedFields = (
  changes: PendingChange[] | undefined
): string[] => {
  if (!changes?.length) return [];
  const seen = new Set<string>();
  const fields: string[] = [];
  for (const change of changes) {
    if (!change.field || seen.has(change.field)) continue;
    seen.add(change.field);
    fields.push(change.field);
  }
  return fields;
};

const InternScheduledBuildCard = ({
  brandId,
  scheduledBuild,
  onRefresh,
  embedded = false,
}: Props) => {
  const snackbarContext = useContext(SnackbarContext);
  const [timeRemaining, setTimeRemaining] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);

  const [triggerBuild, isTriggerInFlight] =
    useMutation<InternScheduledBuildCardTriggerMutation>(triggerBuildMutation);

  const [cancelBuild, isCancelInFlight] =
    useMutation<InternScheduledBuildCardCancelMutation>(cancelBuildMutation);

  useEffect(() => {
    if (!scheduledBuild) return;

    const updateTime = () => {
      setTimeRemaining(formatTimeRemaining(scheduledBuild.scheduledFor));
      setProgress(
        getProgressPercent(
          scheduledBuild.lastChangeAt,
          scheduledBuild.scheduledFor
        )
      );
    };

    updateTime();
    const interval = setInterval(updateTime, 1000 * 60); // Update every minute

    return () => clearInterval(interval);
  }, [scheduledBuild]);

  const handleTrigger = () => {
    if (!scheduledBuild) return;
    triggerBuild({
      variables: { brandId, platform: scheduledBuild.platform },
      onCompleted: (data) => {
        if (data.triggerScheduledBuildNow?.success) {
          snackbarContext?.openSnackbar(
            "Build triggered successfully",
            "success"
          );
          onRefresh?.();
        } else {
          snackbarContext?.openSnackbar(
            data.triggerScheduledBuildNow?.message || "Failed to trigger build",
            "error"
          );
        }
      },
      onError: () => {
        snackbarContext?.openSnackbar("Failed to trigger build", "error");
      },
    });
  };

  const handleCancel = () => {
    if (!scheduledBuild) return;
    cancelBuild({
      variables: { brandId, platform: scheduledBuild.platform },
      onCompleted: (data) => {
        if (data.cancelScheduledBuild?.success) {
          snackbarContext?.openSnackbar("Build cancelled", "success");
          onRefresh?.();
        } else {
          snackbarContext?.openSnackbar(
            data.cancelScheduledBuild?.message || "Failed to cancel build",
            "error"
          );
        }
      },
      onError: () => {
        snackbarContext?.openSnackbar("Failed to cancel build", "error");
      },
    });
  };

  const emptyState = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: embedded ? 2 : 3,
        height: embedded ? "100%" : undefined,
        color: "var(--intern-text-tertiary)",
      }}
    >
      <ScheduleIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
      <Typography variant="body2" sx={{ fontSize: "0.8125rem" }}>
        No scheduled builds
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontSize: "0.6875rem", mt: 0.5, opacity: 0.7 }}
      >
        Changes will trigger an automatic build
      </Typography>
    </Box>
  );

  if (!scheduledBuild) {
    if (embedded) return emptyState;
    return (
      <InternCard title="Scheduled Build" variant="compact">
        {emptyState}
      </InternCard>
    );
  }

  const triggerFields = uniqueChangedFields(scheduledBuild.pendingChanges);
  const isKeepalive = triggerFields.includes("inactivity_keepalive");
  const shownTriggerFields = triggerFields.slice(0, 6);
  const extraTriggerFields = triggerFields.length - shownTriggerFields.length;

  const content = (
    <Stack spacing={2} sx={embedded ? { height: "100%" } : undefined}>
      {embedded ? (
        <Stack direction="row" justifyContent="flex-end">
          <InternStatusBadge
            status="warning"
            label={scheduledBuild.platform.toUpperCase()}
            size="small"
          />
        </Stack>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          borderRadius: 1.5,
          bgcolor: "var(--intern-bg-secondary)",
          border: "1px solid var(--intern-border-default)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: "rgba(255, 152, 0, 0.1)",
          }}
        >
          <AccessTimeIcon sx={{ fontSize: 24, color: "#FF9800" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              color: "var(--intern-text-primary)",
              fontWeight: 700,
              fontSize: "1.25rem",
              fontFamily: "monospace",
            }}
          >
            {timeRemaining}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
            }}
          >
            until build starts
          </Typography>
        </Box>
        <Chip
          label={
            isKeepalive
              ? "Keepalive"
              : `${scheduledBuild.changesCount} change${
                  scheduledBuild.changesCount !== 1 ? "s" : ""
                }`
          }
          size="small"
          sx={{
            bgcolor: isKeepalive ? "#15803d" : "var(--intern-accent)",
            color: "#fff",
            fontWeight: 600,
            fontSize: "0.6875rem",
          }}
        />
      </Box>

      <Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            borderRadius: 2,
            bgcolor: "var(--intern-bg-secondary)",
            "& .MuiLinearProgress-bar": {
              bgcolor: "#FF9800",
              borderRadius: 2,
            },
          }}
        />
      </Box>

      {shownTriggerFields.length > 0 ? (
        <Box>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.75,
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Triggered by
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {shownTriggerFields.map((field) => (
              <Chip
                key={field}
                label={formatFieldName(field)}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  bgcolor: "var(--intern-bg-secondary)",
                  color: "var(--intern-text-secondary)",
                  border: "1px solid var(--intern-border-default)",
                }}
              />
            ))}
            {extraTriggerFields > 0 ? (
              <Chip
                label={`+${extraTriggerFields}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  bgcolor: "var(--intern-bg-secondary)",
                  color: "var(--intern-text-tertiary)",
                }}
              />
            ) : null}
          </Stack>
        </Box>
      ) : null}

      <Stack
        direction="row"
        spacing={1}
        sx={embedded ? { mt: "auto" } : undefined}
      >
        <LoadingButton
          variant="contained"
          size="small"
          loading={isTriggerInFlight}
          startIcon={<PlayArrowIcon />}
          onClick={handleTrigger}
          sx={{
            flex: 1,
            bgcolor: "var(--intern-accent)",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.75rem",
            "&:hover": {
              bgcolor: "var(--intern-accent)",
              filter: "brightness(1.1)",
            },
          }}
        >
          Build Now
        </LoadingButton>
        <LoadingButton
          variant="outlined"
          size="small"
          loading={isCancelInFlight}
          startIcon={<CancelIcon />}
          onClick={handleCancel}
          sx={{
            flex: 1,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.75rem",
            borderColor: "var(--intern-border-default)",
            color: "var(--intern-text-secondary)",
            "&:hover": {
              borderColor: "var(--intern-status-error)",
              color: "var(--intern-status-error)",
              bgcolor: "var(--intern-status-error-bg)",
            },
          }}
        >
          Cancel
        </LoadingButton>
      </Stack>
    </Stack>
  );

  if (embedded) return content;

  return (
    <InternCard
      title="Scheduled Build"
      variant="compact"
      titleAction={
        <InternStatusBadge
          status="warning"
          label={scheduledBuild.platform.toUpperCase()}
          size="small"
        />
      }
    >
      {content}
    </InternCard>
  );
};

export default InternScheduledBuildCard;
